<script setup lang="ts">
import {ref} from "vue"
import JudgeScoreCreator from "components/test/inner/JudgeScoreCreator.vue";
import TestSelectOptionCreator from "components/test/inner/TestSelectOptionCreator.vue";
import CommonSingleImageUploader from "components/common/CommonSingleImageUploader.vue";
import {checkEnglishAndTF, checkEnglishUpperCase, testSubTypeList} from "@/utils/common.ts";
import {ElNotification} from "element-plus";
import {AddQuestionForm} from "@/types";

const emit = defineEmits(["on-add-question"])
const judgeScoreCreator = ref<InstanceType<typeof JudgeScoreCreator>>()
const testSelectOptionCreator = ref<InstanceType<typeof TestSelectOptionCreator>>()
const rightImageUploader = ref<InstanceType<typeof CommonSingleImageUploader>>()
const questionImageUploader = ref<InstanceType<typeof CommonSingleImageUploader>>()
const isShowing = ref(false)

const active = ref(0)
const type = ref(1)
const problem = ref("")
const image = ref("")
const optionList = ref<{ option: string }[]>([{option: ''}, {option: ''}, {option: ''}, {option: ''},])
const right_answer = ref("")
const right_answer_image = ref("")
const parsing = ref("")
const score = ref("")

const onOptionAdd = () => {
  optionList.value.push({option: ''})
}

const onOptionRemove = () => {
  if (optionList.value.length <= 1) {
    return
  }
  optionList.value.pop()
}

const show = () => {
  isShowing.value = true
}

const hide = () => {
  isShowing.value = false
}

const onPrevStep = () => {
  active.value--
}

const onNextStep = () => {
  if (active.value === 0) {
    if (!problem.value) {
      return ElNotification.warning({
        message: "请输入题目标题"
      })
    }
    if (type.value === 1 || type.value === 2 || type.value === 5) {
      if (optionList.value.length == 0) {
        return ElNotification.warning({
          message: "请完善选项"
        })
      }
      for (let i = 0; i < optionList.value.length; i++) {
        if (!optionList.value[i].option) {
          return ElNotification.warning({
            message: "请完善选项"
          })
        }
      }
    }
    if (questionImageUploader.value?.fileList && questionImageUploader.value?.fileList.length > 0) {
      image.value = questionImageUploader.value.fileList[0].url || ''
    }
  }
  if (active.value === 1) {
    if (!right_answer) {
      return ElNotification.warning({
        message: "请输入正确答案"
      })
    }
    if (rightImageUploader.value?.fileList && rightImageUploader.value?.fileList.length > 0) {
      right_answer_image.value = rightImageUploader.value.fileList[0].url || ''
    }
  }
  active.value++
}

const onComplete = () => {
  if (active.value === 1) {
    if (type.value != 0 && !right_answer.value) {
      return ElNotification.warning({
        message: "请输入正确答案"
      })
    }
    if (rightImageUploader.value?.fileList && rightImageUploader.value?.fileList.length > 0) {
      right_answer_image.value = rightImageUploader.value.fileList[0].url || ''
    }
    if (type.value === 1 || type.value === 2) {
      if (!checkEnglishUpperCase(right_answer.value)) {
        return ElNotification.warning({
          message: "选择题正确答案必须为英文大写字母"
        })
      }
      if (type.value === 1) {
        if (right_answer.value.length !== 1) {
          return ElNotification.warning({
            message: "单选题正确答案必须只有一项"
          })
        }
      } else if (type.value === 2) {
        if (right_answer.value.length === 0 || right_answer.value.length === 1) {
          return ElNotification.warning({
            message: "多选题正确答案必须有多项"
          })
        }
      }
    } else if (type.value === 5) {
      if (!checkEnglishAndTF(right_answer.value)) {
        return ElNotification.warning({
          message: "竞赛题正确答案必须为T或F"
        })
      }
      if (right_answer.value.length != optionList.value.length) {
        return ElNotification.warning({
          message: "竞赛题正确答案必须对每个选项做判断"
        })
      }
    }
    const optionTextList = []
    if (type.value === 1 || type.value === 2 || type.value === 5) {
      for (let i = 0; i < optionList.value.length; i++) {
        optionTextList.push(optionList.value[i].option)
      }
    }
    const answer = optionTextList.join(",")
    let score_json = undefined
    if (type.value === 5) {
      if (!judgeScoreCreator.value?.valid()) {
        return ElNotification.warning({
          message: "请完善评分"
        })
      } else {
        const scoreList = judgeScoreCreator.value?.getTestScoreList()
        score_json = JSON.stringify(scoreList)
      }
    }
    const form: AddQuestionForm = {
      type: type.value,
      problem: problem.value,
      answer: answer,
      right_answer: right_answer.value,
      parsing: parsing.value,
      score: score.value,
      score_json: score_json,
      image: image.value,
      right_answer_image: right_answer_image.value,
    }
    emit("on-add-question", form)
  }
}

const onClose = () => {
  active.value = 0
  type.value = 1
  problem.value = ""
  image.value = ""
  optionList.value = [{option: ''}, {option: ''}, {option: ''}, {option: ''},]
  right_answer.value = ""
  right_answer_image.value = ""
  parsing.value = ""
  score.value = ""
}

defineExpose({
  show, hide
})
</script>

<template>
  <el-dialog
      title="添加小题" v-model="isShowing"
      align-center :close-on-click-modal="false"
      destroy-on-close
      @close="onClose">
    <el-steps :active="active">
      <el-step title="填写题目"/>
      <el-step title="填写答案"/>
    </el-steps>
    <div class="mt-1rem">
      <el-form label-position="top" label-width="auto" v-show="active === 0">
        <el-form-item label="题型">
          <el-select v-model="type">
            <el-option v-for="item in testSubTypeList" :label="item.label" :value="item.value">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="标题">
          <el-input type="textarea" v-model="problem" placeholder="请输入题目标题"/>
        </el-form-item>
        <el-form-item label="题图(可选)">
          <common-single-image-uploader ref="questionImageUploader"></common-single-image-uploader>
        </el-form-item>
        <el-form-item label="选项" v-if="type === 1 || type === 2 || type === 5">
          <test-select-option-creator
              ref="testSelectOptionCreator"
              :option-list="optionList"
              @on-option-add="onOptionAdd"
              @on-option-remove="onOptionRemove">
          </test-select-option-creator>
        </el-form-item>
      </el-form>
      <el-form label-position="top" label-width="auto" v-show="active === 1">
        <el-form-item label="答案" v-if="type !== 0">
          <el-input type="textarea" v-model="right_answer" placeholder="请输入题目答案"/>
        </el-form-item>
        <el-form-item label="答案图(可选)" v-if="type !== 0">
          <common-single-image-uploader ref="rightImageUploader"></common-single-image-uploader>
        </el-form-item>
        <el-form-item label="解析(可选)" v-if="type !== 0">
          <el-input type="textarea" v-model="parsing" placeholder="请输入题目解析"/>
        </el-form-item>
        <el-form-item label="分值" v-if="type !== 0">
          <el-input v-model="score" placeholder="请输入题目总分"/>
        </el-form-item>
        <el-form-item label="评分规则" v-if="type === 5">
          <judge-score-creator ref="judgeScoreCreator" :option-num="optionList.length"/>
        </el-form-item>
      </el-form>
    </div>
    <div class="mt-1rem flex justify-end">
      <el-button type="warning" @click="onPrevStep" v-if="active > 0">
        上一步
      </el-button>
      <el-button type="primary" @click="onNextStep" v-if="active < 1">
        下一步
      </el-button>
      <el-button type="primary" @click="onComplete" v-if="active === 1">
        完成
      </el-button>
    </div>
  </el-dialog>
</template>

<style scoped>
</style>
